import classNames from 'classnames';

import { Breakpoint } from '@farmersdog/corgi-x';
import { attributesToQueryString } from '@farmersdog/utils';

import styles from './ImgixPicture.css';

/**
 * This is by no means a complete list. Please refer to the imgix documentation
 * for the complete list.
 *
 * https://docs.imgix.com/apis/rendering
 */
export interface ImgixTransformation {
  /**
   * The format of the image. For complete documentation please see
   * https://docs.imgix.com/apis/rendering/format/fm
   */
  fm?: ImgixFormat;
  /**
   * The fit mode of the image. For complete documentation please see
   * https://docs.imgix.com/apis/rendering/size/fit
   */
  fit?: ImgixFit;
  /**
   * The crop mode of the image. For complete documentation please see
   * https://docs.imgix.com/apis/rendering/size/crop
   */
  crop?: ImgixCrop;
  /**
   * The width of the image. For more complete documentation please see
   * https://docs.imgix.com/apis/rendering/size/w
   */
  w?: number;
  /**
   * The height of the image. For more complete documentation please see
   * https://docs.imgix.com/apis/rendering/size/h
   */
  h?: number;
  /**
   * The pixel ratio of the image. For more complete documentation please see
   * https://docs.imgix.com/apis/rendering/pixel-density/dpr
   */
  dpr?: number;
}

export enum ImgixFormat {
  gif = 'gif',
  jp2 = 'jp2',
  jpg = 'jpg',
  json = 'json',
  jxr = 'jxr',
  pjpg = 'pjpg',
  mp4 = 'mp4',
  png = 'png',
  png8 = 'png8',
  png32 = 'png32',
  webm = 'webm',
  webp = 'webp',
  blurhash = 'blurhash',
}

export enum ImgixFit {
  clamp = 'clamp',
  clip = 'clip',
  crop = 'crop',
  facearea = 'facearea',
  fill = 'fill',
  fillmax = 'fillmax',
  max = 'max',
  min = 'min',
  scale = 'scale',
}

export enum ImgixCrop {
  top = 'top',
  bottom = 'bottom',
  left = 'left',
  right = 'right',
  faces = 'faces',
  focalpoint = 'focalpoint',
  edges = 'edges',
  entropy = 'entropy',
}

export interface Source {
  url: string;
  transformations?: ImgixTransformation;
}

export interface ResponsiveSources {
  xs?: Source;
  sm?: Source;
  md?: Source;
  lg?: Source;
  xl?: Source;
}

export interface ImgixPictureProps {
  src: Source | ResponsiveSources;
  alt: string;
  className?: string;
}

export const mediaQueryValuesBySize = {
  xs: Breakpoint.XS,
  sm: Breakpoint.SM,
  md: Breakpoint.MD,
  lg: Breakpoint.LG,
  xl: Breakpoint.XL,
};

export const supportedPixelDensities = [1, 2];

export const supportedFormats: ImgixFormat[] = [
  ImgixFormat.webp,
  ImgixFormat.jp2,
  ImgixFormat.jpg,
];

export const supportedMediaSizes: (keyof ResponsiveSources)[] = [
  'xl',
  'lg',
  'md',
  'sm',
  'xs',
];

export function ImgixPicture({
  src,
  alt,
  className,
  ...props
}: ImgixPictureProps) {
  const sources: ResponsiveSourceEntry[] = isSingleSource(src)
    ? [['xs', src]]
    : supportedMediaSizes
        .filter(size => Boolean(src[size]))
        .map(size => getResponsiveSourceEntry(size, src));

  return (
    <picture>
      {supportedFormats
        .map(format => {
          return sources.map(([size, source]) => {
            const type = `image/${format}`;
            const [baseSrcUrl, baseSrcQuery] = source.url.split('?');
            const searchParams = new URLSearchParams(baseSrcQuery);
            const baseQuery: Record<string, string> = {};

            for (const [key, value] of searchParams.entries()) {
              if (value) {
                baseQuery[key] = value;
              }
            }

            const media = `(min-width: ${mediaQueryValuesBySize[size]})`;
            const key = `${size}-${type}`;

            const srcSet = supportedPixelDensities
              .map(pixelDensity => {
                const queryString = attributesToQueryString(
                  {
                    ...baseQuery,
                    ...source.transformations,
                    fm: format,
                    dpr: pixelDensity,
                  },
                  {
                    skipEncoding: true,
                  }
                );

                const imageSource = `${baseSrcUrl}?${queryString}`;
                const pixelDensityDescriptor = `${String(pixelDensity)}x`;

                return `${imageSource} ${pixelDensityDescriptor}`;
              })
              .join(',');

            return (
              <source key={key} media={media} type={type} srcSet={srcSet} />
            );
          });
        })
        .flat(1)}

      <img alt={alt} className={classNames(styles.img, className)} {...props} />
    </picture>
  );
}

type ResponsiveSourceEntry = [size: keyof ResponsiveSources, source: Source];

function getResponsiveSourceEntry(
  size: keyof ResponsiveSources,
  sources: ResponsiveSources
): ResponsiveSourceEntry {
  const source = sources[size];

  if (!source) {
    throw new Error(`Cannot find source of size ${size}`);
  }

  return [size, source];
}

function isSingleSource(src: Source | ResponsiveSources): src is Source {
  return Object.hasOwnProperty.call(src, 'url');
}
